<script setup>
import {ref, computed, onMounted, onUnmounted, watch, nextTick} from 'vue'
import {useStore} from 'vuex'
import * as z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { MapSource, MapLayer } from '@/components/map'
import { Spinner } from '@/components/ui/spinner'
import {CheckCircle2, XCircle} from 'lucide-vue-next'
import { HOST, API } from '@/utils/http'
import { debounce } from 'lodash'
import { useRouter } from 'vue-router'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const store = useStore()
const router = useRouter()
const map = computed(() => store.getters['geo/getMap'])
const layersToken = computed(() => store.getters['geo/getTilesToken'])
const projects = computed(() => store.getters['project/getProjects'])
const storeTemplates = computed(() => store.getters['geo/getProjectReportTemplates'])
const selectedRange = computed(() => store.getters['layers/getSelectedRange'])

const emit = defineEmits(['isLoaded', 'datesLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  },
  layer: {
    type: Object,
    required: false,
    default: () => ({})
  },
  code: {
    type: String,
    required: true
  }
})

const isSubmitting = ref(false)
const isUpdating = ref(false)
const selectedProjects = ref([])
const templateOptions = ref([])
const selectedTemplates = ref([])
const isLoadingTemplates = ref(false)
const isLoadingDates = ref(false)
const layerDates = ref([])
const features = ref([])
const featureCollection = ref({
  type: 'FeatureCollection',
  features: []
})
const isLoadingFeatures = ref(false)
const featuresCount = ref(0)
const errorMessage = ref('')
const layerColors = {
  point: '#0059ff',
  line: '#00a3ff',
  polygon: '#7a00ff'
}

const sourceId = computed(() => `features-source-${props.code}`)
const pointLayerId = computed(() => `features-points-${props.code}`)
const lineLayerId = computed(() => `features-lines-${props.code}`)
const polygonLayerId = computed(() => `features-polygons-${props.code}`)

const pointLayerStyle = {
  'circle-radius': [
    'interpolate', ['linear'], ['zoom'],
    8, 4,
    12, 6,
    16, 8
  ],
  'circle-color': ['coalesce', ['get', 'color'], layerColors.point],
  'circle-stroke-width': 1.5,
  'circle-stroke-color': '#ffffff',
  'circle-opacity': 0.8
}

const lineLayerStyle = {
  'line-width': [
    'interpolate', ['linear'], ['zoom'],
    8, 2,
    12, 3,
    16, 4
  ],
  'line-color': ['coalesce', ['get', 'color'], layerColors.line],
  'line-opacity': 0.8
}

const polygonLayerStyle = {
  'fill-color': ['coalesce', ['get', 'color'], layerColors.polygon],
  'fill-opacity': 0.4,
  'fill-outline-color': '#ffffff'
}

const formSchema = toTypedSchema(z.object({
  projectIds: z.array(z.string()).min(1, "At least one project must be selected"),
  templateIds: z.array(z.string()).optional(),
}))

const { handleSubmit, setValues, values: formValues } = useForm({
  validationSchema: formSchema,
})

const debouncedFetchFeatures = debounce(async () => {
  await fetchFeatures()
}, 300)

const processDateFormat = (dateItem) => {
  if (dateItem.date && !dateItem.date.endsWith('Z') && !dateItem.date.includes('+')) {
    dateItem.date = dateItem.date + 'Z';
  }
  return dateItem;
}

const fetchDates = async () => {
  try {
    isLoadingDates.value = true

    const response = await API({
      method: 'GET',
      url: `${HOST}/layers/${props.code}/radar/dates`
    })

    if (response.data && response.data.layers && Array.isArray(response.data.layers)) {
      const templateLayers = response.data.layers.find(layer => layer.id === 'features' && layer.layers);

      if (templateLayers && templateLayers.layers) {
        const processedLayers = templateLayers.layers.map(layer => {
          const processedDates = Array.isArray(layer.dates)
            ? layer.dates.map(processDateFormat)
            : [];

          return {
            id: layer.id || 'unknown',
            name: layer.name || 'Unknown Template',
            color: layer.color || 'blue',
            dates: processedDates
          };
        });

        layerDates.value = processedLayers
        emit('datesLoaded', processedLayers)

        if (processedLayers.length > 0) {
          try {
            store.commit('layers/ADD_DATES', processedLayers);
          } catch (storeError) {
            console.error('Error updating store with dates:', storeError)
          }
        }
      } else {
        const transformedLayers = response.data.layers.map(layer => {
          const processedDates = Array.isArray(layer.dates)
            ? layer.dates.map(processDateFormat)
            : [];

          return {
            id: layer.id || 'unknown',
            name: layer.name || (layer.id === 'features' ? 'Features' : layer.id),
            color: layer.color || 'blue',
            dates: processedDates
          };
        });

        layerDates.value = transformedLayers;
        emit('datesLoaded', transformedLayers);

        if (transformedLayers.length > 0) {
          try {
            store.commit('layers/ADD_DATES', transformedLayers);
          } catch (storeError) {
            console.error('Error updating store with dates:', storeError);
          }
        }
      }
    } else if (response.data && response.data.dates) {
      const dates = Array.isArray(response.data.dates) ? response.data.dates : [];
      const processedDates = dates.map(processDateFormat);

      const transformedData = {
        id: "features",
        name: "Features",
        color: "green",
        dates: processedDates
      };

      layerDates.value = [transformedData];
      emit('datesLoaded', [transformedData]);

      try {
        store.commit('layers/ADD_DATES', [transformedData]);
      } catch (storeError) {
        console.error('Error updating store with dates:', storeError);
      }
    }
  } catch (error) {
    console.error('Error fetching dates:', error)
    toast({
      title: 'Error loading dates',
      description: 'Failed to fetch dates for the layer',
      variant: 'destructive'
    })
  } finally {
    isLoadingDates.value = false
  }
}

const fetchTemplates = async (projectIds) => {
  if (!projectIds || !projectIds.length) {
    templateOptions.value = []
    return
  }

  isLoadingTemplates.value = true
  templateOptions.value = []

  try {
    const templatesMap = new Map();

    for (const projectId of projectIds) {
      store.commit('geo/SET_REPORT_TEMPLATES', null);
      await store.dispatch('geo/fetchProjectReportTemplates', projectId);
      const projectTemplates = store.getters['geo/getProjectReportTemplates'];

      if (projectTemplates) {
        projectTemplates.forEach(template => {
          templatesMap.set(template.id, template);
        });
      }
    }

    templateOptions.value = Array.from(templatesMap.values());
  } catch (error) {
    console.error('Error fetching templates:', error)
    toast({
      title: 'Error loading templates',
      description: 'Failed to fetch templates for selected projects',
      variant: 'destructive'
    })
  } finally {
    isLoadingTemplates.value = false
  }
}

const fetchFeatures = async () => {
  store.commit('SET_IS_LOADING_DATA', true)
  if (!selectedProjects.value.length) {
    resetFeatures();
    return;
  }

  if (!selectedRange.value || !Array.isArray(selectedRange.value) || selectedRange.value.length !== 2) {
    resetFeatures();
    return;
  }

  isLoadingFeatures.value = true;
  resetFeatures();

  try {
    const projectIds = selectedProjects.value.map(id => parseInt(id));
    const templateIds = selectedTemplates.value.length > 0 ? selectedTemplates.value.map(id => parseInt(id)) : [];
    const startDate = selectedRange.value[0];
    const endDate = selectedRange.value[1];

    const response = await API({
      method: 'POST',
      url: `${HOST}/layers/${props.code}/features`,
      data: {
        start_date: startDate,
        end_date: endDate,
        project_ids: projectIds,
        template_ids: templateIds
      }
    })

    if (response.data && response.data.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      features.value = response.data.features;
      featureCollection.value = response.data;
      featuresCount.value = response.data.count || response.data.features.length;
    }
  } catch (error) {
    console.error('Error fetching features:', error);
    errorMessage.value = 'Failed to fetch features';

    let errorMsg = 'Failed to fetch features for the selected filters and date range';
    if (error.response?.data?.detail) {
      errorMsg = error.response.data.detail;
    } else if (error.request) {
      errorMsg = 'No response from server. Please check your connection.';
    }

    toast({
      title: 'Error loading features',
      description: errorMsg,
      variant: 'destructive'
    });
  } finally {
    store.commit('SET_IS_LOADING_DATA', false)
    isLoadingFeatures.value = false
  }
}

const resetFeatures = () => {
  features.value = [];
  featureCollection.value.features = [];
  featuresCount.value = 0;
  errorMessage.value = '';
}

const updateLayerData = async () => {
  isUpdating.value = true;

  try {
    const projectIds = selectedProjects.value.map(id => parseInt(id));
    const templateIds = selectedTemplates.value.length > 0 ? selectedTemplates.value.map(id => parseInt(id)) : [];

    await API({
      method: 'PATCH',
      url: `${HOST}/layers/${props.code}/collected-data`,
      data: {
        project_ids: projectIds,
        template_ids: templateIds
      }
    });

    toast({
      title: 'Layer updated',
      description: 'Data collection settings updated successfully',
      variant: 'default'
    });

    await fetchDates();
    await fetchFeatures();
  } catch (error) {
    console.error('Error updating layer:', error);
    toast({
      title: 'Update failed',
      description: 'Failed to update collected data settings',
      variant: 'destructive'
    });
  } finally {
    isUpdating.value = false;
  }
}

const onSubmit = handleSubmit(updateLayerData);

const toggleProject = async (field, projectId) => {
  const currentValues = [...(formValues.projectIds || [])];
  const index = currentValues.indexOf(projectId);

  if (index === -1) {
    currentValues.push(projectId);
  } else {
    currentValues.splice(index, 1);
  }

  selectedProjects.value = currentValues;
  setValues({
    projectIds: currentValues,
    templateIds: formValues.templateIds
  });

  await updateLayerData();
}

const handleProjectChange = async (newValue) => {
  resetFeatures();
  selectedProjects.value = newValue;

  setValues({
    projectIds: newValue,
    templateIds: formValues.templateIds || []
  });

  await fetchTemplates(newValue);

  if (formValues.templateIds?.length > 0) {
    const validTemplateIds = templateOptions.value.map(t => t.id.toString());
    const validatedTemplateIds = formValues.templateIds.filter(id => validTemplateIds.includes(id));

    if (validatedTemplateIds.length !== formValues.templateIds.length) {
      selectedTemplates.value = validatedTemplateIds;
      setValues({
        projectIds: newValue,
        templateIds: validatedTemplateIds
      });
    }
  }

  await updateLayerData();
}

const handleTemplateChange = async (newValue) => {
  resetFeatures();
  selectedTemplates.value = newValue || [];

  setValues({
    projectIds: formValues.projectIds,
    templateIds: newValue || []
  });

  await updateLayerData();
}

const toggleTemplate = async (field, templateId) => {
  const currentValues = [...(formValues.templateIds || [])];
  const index = currentValues.indexOf(templateId);

  if (index === -1) {
    currentValues.push(templateId);
  } else {
    currentValues.splice(index, 1);
  }

  selectedTemplates.value = currentValues;
  setValues({
    projectIds: formValues.projectIds,
    templateIds: currentValues
  });

  await updateLayerData();
}

const loadInitialData = async () => {
  let projectIds = [];
  let templateIds = [];

  if (props.layer?.data) {
    if (props.layer.data.project_ids && Array.isArray(props.layer.data.project_ids)) {
      projectIds = props.layer.data.project_ids.map(id => id.toString());
    }

    if (props.layer.data.template_ids && Array.isArray(props.layer.data.template_ids)) {
      templateIds = props.layer.data.template_ids.map(id => id.toString());
    }
  }

  selectedProjects.value = projectIds;
  selectedTemplates.value = templateIds;

  setValues({
    projectIds,
    templateIds
  });

  if (projectIds.length > 0) {
    await fetchTemplates(projectIds);

    if (templateIds.length > 0) {
      const validTemplateIds = templateOptions.value.map(t => t.id.toString());
      const validatedTemplateIds = templateIds.filter(id => validTemplateIds.includes(id));

      if (validatedTemplateIds.length !== templateIds.length) {
        selectedTemplates.value = validatedTemplateIds;
        setValues({
          projectIds,
          templateIds: validatedTemplateIds
        });
      }
    }
  }

  emit('isLoaded', true);
  await fetchDates();

  if (projectIds.length > 0 && selectedRange.value && Array.isArray(selectedRange.value) && selectedRange.value.length === 2) {
    await fetchFeatures();
  }
}

onMounted(loadInitialData);

watch(selectedRange, (newRange, oldRange) => {
  if (newRange !== oldRange && Array.isArray(newRange) && newRange.length === 2) {
    debouncedFetchFeatures();
  }
}, { deep: true });

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    return new Date(dateString).toLocaleDateString();
  } catch (error) {
    return dateString || 'N/A';
  }
}
</script>

<template>
  <MapSource
    v-if="featuresCount > 0 && map"
    :map="map"
    :id="sourceId"
    type="geojson"
    :data="featureCollection"
  >
    <MapLayer
      v-if="map"
      :map="map"
      :id="pointLayerId"
      :source="sourceId"
      type="circle"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Point'],
        layout: {},
        paint: pointLayerStyle
      }"
    />

    <MapLayer
      v-if="map"
      :map="map"
      :id="lineLayerId"
      :source="sourceId"
      type="line"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: {},
        paint: lineLayerStyle
      }"
    />

    <MapLayer
      v-if="map"
      :map="map"
      :id="polygonLayerId"
      :source="sourceId"
      type="fill"
      :before-id="beforeId"
      :group-id="groupId"
      :options="{
        filter: ['==', ['geometry-type'], 'Polygon'],
        layout: {},
        paint: polygonLayerStyle
      }"
    />
  </MapSource>

  <Form
    class="mt-2 pb-2 pr-2"
    :validation-schema="formSchema"
    @submit="onSubmit">
    <FormField v-slot="{ field, componentField }" name="projectIds">
      <FormItem>
        <FormLabel>Project</FormLabel>
        <FormControl>
          <Select v-bind="componentField" multiple @update:modelValue="handleProjectChange" :modelValue="formValues.projectIds">
            <SelectTrigger class="h-auto min-h-[40px] flex-wrap">
              <SelectValue placeholder="Select projects">
                <div class="flex flex-wrap gap-1">
                  <div v-for="projectId in formValues.projectIds" :key="projectId"
                       class="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1">
                    {{ projects.find(p => p.project_id.toString() === projectId)?.project_name }}
                  </div>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="project in projects"
                  :key="project.project_id"
                  :value="project.project_id.toString()"
                >
                  {{ project.project_name }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ field, componentField }" name="templateIds">
      <FormItem>
        <FormLabel>Report Type (Optional)</FormLabel>
        <FormControl>
          <div>
            <Select v-bind="componentField" multiple :disabled="isLoadingTemplates || templateOptions.length === 0"
                   :modelValue="formValues.templateIds" @update:modelValue="handleTemplateChange">
              <SelectTrigger class="h-auto min-h-[40px] flex-wrap">
                <SelectValue :placeholder="isLoadingTemplates ? 'Loading templates...' : templateOptions.length === 0 ? 'Select projects first' : 'Select templates (optional)'">
                  <div v-if="isLoadingTemplates" class="flex items-center gap-2">
                    <Spinner class="h-4 w-4" /> Loading templates...
                  </div>
                  <div v-else class="flex flex-wrap gap-1">
                    <div v-for="templateId in formValues.templateIds" :key="templateId"
                         class="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1">
                      {{ templateOptions.find(t => t.id.toString() === templateId)?.name }}
                    </div>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <div v-if="isLoadingTemplates" class="flex items-center justify-center py-2">
                  <Spinner class="h-4 w-4 mr-2" /> Loading templates...
                </div>
                <div v-else-if="templateOptions.length === 0" class="p-2 text-center text-muted-foreground">
                  Select a project first to load templates
                </div>
                <SelectGroup v-else>
                  <SelectItem
                    v-for="template in templateOptions"
                    :key="template.id"
                    :value="template.id.toString()"
                  >
                    {{ template.name }}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <div v-if="!isLoadingFeatures && formValues.projectIds?.length > 0 && selectedRange && selectedRange.length === 2" class="mt-3">
      <div>
        <div v-if="featuresCount > 0" class="flex flex-col">
          <div class="flex items-center">
            {{ featuresCount }} reports found
          </div>
          <ul class="mt-1 space-y-1">
            <li v-for="(feature, index) in features" :key="index" class="px-1.5 py-0.5 bg-background rounded border flex items-center justify-between">
              <div>
                <div class="flex items-center">
                  <div
                    class="w-3 h-3 rounded-full mr-2"
                    :style="`background-color: ${feature.properties.color || feature.properties.template_color || '#0059ff'}`">
                  </div>
                  <span class="text-sm">{{ feature.properties.name }}</span>
                </div>
                <div class="text-xs text-muted-foreground flex flex-wrap gap-2">
                  <span>Created: {{ formatDate(feature.properties.created_at) }}</span>
                  <span v-if="feature.properties.updated_at">•</span>
                  <span v-if="feature.properties.updated_at">Updated: {{ formatDate(feature.properties.updated_at) }}</span>
                </div>
                <div v-if="feature.properties.data" class="mt-2 text-xs">
                  <div v-if="typeof feature.properties.data === 'object'" class="flex flex-wrap gap-1">
                    <span
                      v-for="(value, key) in feature.properties.data"
                      :key="key"
                      class="px-1.5 py-0.5 bg-muted rounded"
                    >
                      <span>{{ value.key }}: {{ value.value }}</span>
                    </span>
                  </div>
                  <div v-else class="px-1.5 py-0.5 bg-muted rounded">
                    {{ feature.properties.data }}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                @click="router.push(`/projects/${feature.properties.project_id}/features/${feature.properties.code}`)"
              >Open</Button>
            </li>
          </ul>
        </div>
        <div v-else>
          No features found for the selected date range
        </div>
      </div>
    </div>
  </Form>
</template>
